import { reactive } from 'vue';

import type { AuthUserSummary, TokenPair } from '@midiautolistening/contracts';

/**
 * The session, held outside Pinia on purpose.
 *
 * `lib/api.ts` needs the access token inside an axios interceptor, and `lib/ws.ts`
 * needs it before any component mounts. Reading it from a Pinia store would make
 * both modules depend on an active Pinia instance and would close a cycle
 * (store -> api -> store). A plain reactive object has neither problem, and the
 * auth store wraps it so components still see one source of truth.
 *
 * Tokens live in `localStorage`. That is a deliberate trade: it survives a reload,
 * which a SPA behind a login needs, and it is readable by any script that manages
 * to run on this origin. The access token is short-lived (see `expiresIn`) and the
 * refresh token rotates on every use, so a stolen pair is detectable server-side —
 * but nothing here substitutes for keeping XSS out of the bundle.
 */

const STORAGE_KEY = 'midiautolistening.session.v1';

/** Refresh this many milliseconds before the access token actually lapses. */
export const ACCESS_TOKEN_REFRESH_MARGIN_MS = 60_000;

export interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  /** Epoch milliseconds at which the access token stops being accepted. */
  accessTokenExpiresAt: number | null;
  user: AuthUserSummary | null;
}

export const session = reactive<SessionState>({
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null,
  user: null,
});

interface PersistedSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number;
  user: AuthUserSummary | null;
}

function persist(): void {
  if (!session.accessToken || !session.refreshToken || !session.accessTokenExpiresAt) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  const payload: PersistedSession = {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    accessTokenExpiresAt: session.accessTokenExpiresAt,
    user: session.user,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Private-browsing quota errors must not break the app; the session simply
    // does not survive a reload.
  }
}

/** Rehydrate on boot. Malformed storage is discarded rather than trusted. */
export function loadPersistedSession(): void {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.refreshToken !== 'string') {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    session.accessToken = parsed.accessToken;
    session.refreshToken = parsed.refreshToken;
    session.accessTokenExpiresAt =
      typeof parsed.accessTokenExpiresAt === 'number' ? parsed.accessTokenExpiresAt : null;
    session.user = parsed.user ?? null;
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

export function applyTokens(tokens: TokenPair): void {
  session.accessToken = tokens.accessToken;
  session.refreshToken = tokens.refreshToken;
  session.accessTokenExpiresAt = Date.now() + tokens.expiresIn * 1_000;
  persist();
}

export function setSessionUser(user: AuthUserSummary | null): void {
  session.user = user;
  persist();
}

export function clearSession(): void {
  session.accessToken = null;
  session.refreshToken = null;
  session.accessTokenExpiresAt = null;
  session.user = null;
  persist();
}

export function isAuthenticated(): boolean {
  return session.accessToken !== null && session.refreshToken !== null;
}

/** True when the access token is gone or about to lapse. */
export function accessTokenNeedsRefresh(): boolean {
  if (!session.accessToken) return true;
  if (session.accessTokenExpiresAt === null) return false;
  return Date.now() >= session.accessTokenExpiresAt - ACCESS_TOKEN_REFRESH_MARGIN_MS;
}

// ---------------------------------------------------------------------------
// Expiry notification
// ---------------------------------------------------------------------------

type SessionExpiredListener = () => void;

const expiredListeners = new Set<SessionExpiredListener>();

/**
 * Registered by `main.ts` to route to /login. Kept as a callback so `lib/api.ts`
 * never imports the router — the router imports the stores, which import the api.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

export function notifySessionExpired(): void {
  for (const listener of expiredListeners) {
    listener();
  }
}
